<script setup lang="ts">
/** System: all jobs, versions and health. Key rotation and migrations run from the operator CLI. */
import { Activity, ListChecks, Package, Terminal, Users } from "@lucide/vue";
import { useQuery } from "@tanstack/vue-query";
import { computed } from "vue";
import { usePrincipals } from "@/api/admin";
import { isRunning, useJobs } from "@/api/jobs";
import CopyField from "@/components/common/CopyField.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import FilterChips from "@/components/common/FilterChips.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import SectionCard from "@/components/common/SectionCard.vue";
import StatCard from "@/components/common/StatCard.vue";
import JobItem from "@/components/items/JobItem.vue";
import { useRouteQuery } from "@/composables/useRouteQuery";
import { useSession } from "@/composables/useSession";
import { t } from "@/copy/strings";
import { formatNumber } from "@/lib/format";

definePage({ meta: { title: "System", requires: { adminArea: "system" } } });

const { can } = useSession();
const appVersion = __APP_VERSION__;
const buildTime = __BUILD_TIME__;
const status = useRouteQuery("status");
const jobs = useJobs(() => ({ all: true, status: status.value || undefined }));
const running = computed(() => (jobs.data.value ?? []).filter(isRunning).length);
const principals = usePrincipals("", () => can("admin.users"));

const api = useQuery({
  queryKey: ["system", "openapi"],
  queryFn: async () => ((await (await fetch("/api/v2/openapi.json")).json()) as { info?: { version?: string }; paths?: Record<string, unknown> }),
  staleTime: 10 * 60_000,
});
const ready = useQuery({
  queryKey: ["system", "readyz"],
  queryFn: async () => (await fetch("/readyz")).ok,
  refetchInterval: 30_000,
});

const serverState = computed(() =>
  ready.data.value === false
    ? { label: "Not ready", tone: "danger" as const }
    : ready.data.value
      ? { label: "Ready", tone: "success" as const }
      : { label: "Checking…", tone: "default" as const },
);
const apiHint = computed(() => {
  const info = api.data.value;
  if (!info) return "API …";
  return `API ${info.info?.version ?? "?"} · ${formatNumber(Object.keys(info.paths ?? {}).length)} routes`;
});

const statusOptions = [
  { value: "working", label: "Running" },
  { value: "queued", label: "Queued" },
  { value: "input_required", label: "Waiting" },
  { value: "failed", label: "Failed" },
  { value: "completed", label: "Completed" },
];
const statusModel = computed({ get: () => status.value || undefined, set: (v: string | string[] | undefined) => (status.value = (v as string) ?? "") });
</script>

<template>
  <div>
    <PageHeader :crumbs="[{ label: t.admin.title, to: '/admin' }]" :title="t.admin.system" :description="t.admin.systemDescription" />

    <div class="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
      <StatCard label="Server" :value="serverState.label" :icon="Activity" :tone="serverState.tone" :loading="ready.isPending.value" hint="Health check every 30 s" />
      <StatCard label="Jobs running" :value="formatNumber(running)" :icon="ListChecks" :tone="running ? 'info' : 'default'" :loading="jobs.isPending.value" hint="Across everyone" />
      <StatCard
        v-if="can('admin.users')"
        label="People"
        :value="principals.data.value ? formatNumber(principals.data.value.length) : null"
        :icon="Users"
        :loading="principals.isPending.value"
        to="/admin/access/people"
        hint="who used Smake"
      />
      <StatCard label="Version" :value="appVersion" :icon="Package" :hint="apiHint">
        <p class="text-xs text-muted-foreground">Web built <RelativeTime :value="buildTime" /></p>
      </StatCard>
    </div>

    <div class="grid grid-cols-1 gap-6 *:min-w-0 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
      <SectionCard title="Everyone's jobs" :icon="ListChecks">
        <FilterChips v-model="statusModel" :options="statusOptions" label="Status" class="mb-3" />
        <ListSkeleton v-if="jobs.isPending.value" :rows="5" />
        <ErrorState v-else-if="jobs.error.value" compact :error="jobs.error.value" @retry="jobs.refetch()" />
        <EmptyState v-else-if="!jobs.data.value?.length" compact :icon="ListChecks" :title="status ? 'No jobs with this status' : 'No jobs'" />
        <div v-else class="flex flex-col gap-2">
          <JobItem v-for="job in jobs.data.value.slice(0, 50)" :key="job.id" :job="job" />
        </div>
      </SectionCard>

      <SectionCard title="Operator tasks" :icon="Terminal" description="These run from the operator CLI inside a server pod, not from the browser.">
        <div class="flex flex-col gap-3 text-sm">
          <div class="flex flex-col gap-1.5">
            <p class="font-medium">Rotate the encryption key</p>
            <CopyField prompt value="npm run cli -- rotate-key" label="rotate key command" />
          </div>
          <div class="flex flex-col gap-1.5">
            <p class="font-medium">Run database migrations</p>
            <CopyField prompt value="npm run cli -- migrate" label="migrate command" />
          </div>
          <div class="flex flex-col gap-1.5">
            <p class="font-medium">Re-seed built-in roles (empty database only)</p>
            <CopyField prompt value="npm run cli -- seed-roles" label="seed roles command" />
          </div>
        </div>
      </SectionCard>
    </div>
  </div>
</template>
